import { SIM_HZ } from '../../../core/GameLoop';
import { Mod } from '../BulletPool';
import { bulletKindIndex, type BulletLane, type BulletShape } from '../bulletKinds';

/**
 * Data-driven bullet pattern authoring. Patterns are plain typed TS objects;
 * `compilePattern` converts all times (seconds) to integer sim steps once, so
 * the runtime compares integers exactly — no float drift, fully deterministic.
 */

export type AngleSpec =
  | { kind: 'fixed'; angle: number }
  /** Spiral: angle = start + rate * secondsSinceEmitterStart (rad/s). */
  | { kind: 'spin'; start: number; rate: number }
  /** At the player at fire time. Reactive: reads the world, consumes NO rng. */
  | { kind: 'aimed'; offset?: number }
  /** Uniform in center ± arc/2. Draws once per volley from the pattern rng. */
  | { kind: 'randomArc'; center: AngleSpec; arc: number };

export interface ShotSpec {
  /** N bullets evenly over armsArc (default full circle) — rings/fans. */
  arms?: number;
  armsArc?: number;
  /** Extra bullets per arm fanned over `spread` radians. */
  spreadCount?: number;
  spread?: number;
  /** Suppress bullets inside gap arcs — walls with dodge holes. */
  gap?: {
    count: number;
    width: number;
    placement: 'random' | 'aimedSafe' | 'fixed';
    at?: number;
  };
  /** Per-bullet angle noise in ± jitter (pattern rng). */
  jitter?: number;
}

export type BulletModSpec =
  | { kind: 'curve'; rate: number }
  | { kind: 'homing'; turnRate: number; duration: number }
  | { kind: 'gravity'; g: number }
  | { kind: 'delayedAim'; at: number; speed: number };

export interface BulletSpec {
  shape: BulletShape;
  lane?: BulletLane; // default pink (enemy)
  speed: number | { from: number; to: number; over: number };
  radius: number;
  lifetime: number;
  scale?: number;
  mod?: BulletModSpec;
}

export interface TelegraphSpec {
  kind: 'zone' | 'line';
  /** Seconds of warning before the volley fires / zone commits. */
  lead: number;
  /** zone: snapshot the player's position at telegraph START (commit point). */
  aimed?: boolean;
  radius?: number; // zone
  length?: number; // line
  width?: number; // line
  /** zone only: damage dealt inside the zone at commit. */
  damage?: number;
}

export interface EmitterDef {
  delay?: number;
  /** Seconds of firing; alternative to `volleys`. */
  duration?: number;
  interval: number;
  volleys?: number;
  origin?: { offset: [number, number] } | { orbit: { radius: number; rate: number } };
  angle: AngleSpec;
  shot?: ShotSpec;
  /** Omit for telegraph-only emitters (ground slams). */
  bullet?: BulletSpec;
  telegraph?: TelegraphSpec;
}

export interface PatternDef {
  id: string;
  /** Seconds; bounds emitters that specify neither volleys nor duration. */
  duration?: number;
  emitters: EmitterDef[];
}

// ---------------------------------------------------------------------------
// Compiled representation (integer steps, precomputed mod params)

export interface CompiledBullet {
  kind: number;
  speed: number;
  radius: number;
  lifeSteps: number;
  scale: number;
  modKind: number;
  p0: number;
  p1: number;
  p2: number;
}

export interface CompiledEmitter {
  delaySteps: number;
  intervalSteps: number;
  volleys: number;
  leadSteps: number;
  originOffset: [number, number] | null;
  orbit: { radius: number; rateStep: number } | null;
  angle: AngleSpec;
  shot: ShotSpec | null;
  bullet: CompiledBullet | null;
  telegraph: TelegraphSpec | null;
}

export interface CompiledPattern {
  id: string;
  durationSteps: number;
  emitters: CompiledEmitter[];
}

export function toSteps(seconds: number): number {
  return Math.max(0, Math.round(seconds * SIM_HZ));
}

export function compilePattern(def: PatternDef): CompiledPattern {
  const emitters = def.emitters.map((e) => compileEmitter(e, def));
  let durationSteps = def.duration !== undefined ? toSteps(def.duration) : 0;
  for (const e of emitters) {
    const extent = e.delaySteps + (e.volleys - 1) * e.intervalSteps + 1;
    if (extent > durationSteps) durationSteps = extent;
  }
  return { id: def.id, durationSteps, emitters };
}

function compileEmitter(e: EmitterDef, pattern: PatternDef): CompiledEmitter {
  const leadSteps = e.telegraph ? toSteps(e.telegraph.lead) : 0;
  // A telegraphed volley needs its warning to fit before it fires — bump the
  // emitter delay so the FIRST volley gets the full lead too.
  const delaySteps = Math.max(toSteps(e.delay ?? 0), leadSteps);
  const intervalSteps = Math.max(1, toSteps(e.interval));

  let volleys: number;
  if (e.volleys !== undefined) {
    volleys = e.volleys;
  } else if (e.duration !== undefined) {
    volleys = Math.floor(toSteps(e.duration) / intervalSteps) + 1;
  } else if (pattern.duration !== undefined) {
    volleys = Math.floor(Math.max(0, toSteps(pattern.duration) - delaySteps) / intervalSteps) + 1;
  } else {
    throw new Error(`Pattern ${pattern.id}: emitter needs volleys, duration, or pattern duration`);
  }

  let bullet: CompiledBullet | null = null;
  if (e.bullet) {
    const b = e.bullet;
    let speed: number;
    let modKind: number = Mod.None;
    let p0 = 0;
    let p1 = 0;
    let p2 = 0;
    if (typeof b.speed === 'number') {
      speed = b.speed;
    } else {
      speed = b.speed.from;
      modKind = Mod.Accel;
      p0 = (b.speed.to - b.speed.from) / b.speed.over;
      p2 = b.speed.to;
    }
    if (b.mod) {
      if (modKind !== Mod.None) {
        throw new Error(`Pattern ${pattern.id}: speed ramp and mod are mutually exclusive`);
      }
      switch (b.mod.kind) {
        case 'curve':
          modKind = Mod.Curve;
          p0 = b.mod.rate;
          break;
        case 'homing':
          modKind = Mod.Homing;
          p0 = b.mod.turnRate;
          p1 = toSteps(b.mod.duration);
          break;
        case 'gravity':
          modKind = Mod.Gravity;
          p0 = b.mod.g;
          break;
        case 'delayedAim':
          modKind = Mod.DelayedAim;
          p1 = toSteps(b.mod.at);
          p2 = b.mod.speed;
          break;
      }
    }
    bullet = {
      kind: bulletKindIndex(b.shape, b.lane ?? 'pink'),
      speed,
      radius: b.radius,
      lifeSteps: Math.min(65535, toSteps(b.lifetime)),
      scale: b.scale ?? 1,
      modKind,
      p0,
      p1,
      p2,
    };
  }

  return {
    delaySteps,
    intervalSteps,
    volleys,
    leadSteps,
    originOffset: e.origin && 'offset' in e.origin ? e.origin.offset : null,
    orbit: e.origin && 'orbit' in e.origin
      ? { radius: e.origin.orbit.radius, rateStep: e.origin.orbit.rate / SIM_HZ }
      : null,
    angle: e.angle,
    shot: e.shot ?? null,
    bullet,
    telegraph: e.telegraph ?? null,
  };
}
